import type { PopoverProps } from '@arco-design/web-react';
import type { TooltipProps } from '../Tooltip';
import type { MenuGroup, MenuItem, MenuNormalItem, MenuProps, MenuSubMenu } from './const';
import { Popover, Trigger } from '@arco-design/web-react';
import { cn } from '@utils';
import { forwardRef, Fragment } from 'react';
import Tooltip from '../Tooltip';
import './index.less';

function Menu(props: MenuProps, ref: React.Ref<HTMLDivElement>) {
  const { menuItems, className, menuItemClassName, onAction, style } = props;

  const handleClick = (e: React.MouseEvent, item: MenuNormalItem) => {
    item.onClick?.(e, item);
    onAction?.();
  };

  const renderNormalItem = (item: MenuNormalItem) => {
    if (item.visible === false) {
      return null;
    }

    if (item.render) {
      return (
        <div
          key={item.key}
          onMouseEnter={(e: React.MouseEvent) => !item.disabled && item.onMouseEnter?.(e, item)}
          onMouseLeave={(e: React.MouseEvent) => !item.disabled && item.onMouseLeave?.(e, item)}
        >
          {item.render({ onAction })}
        </div>
      );
    }
    if (item.component) {
      return (
        <div
          key={item.key}
          onMouseEnter={(e: React.MouseEvent) => !item.disabled && item.onMouseEnter?.(e, item)}
          onMouseLeave={(e: React.MouseEvent) => !item.disabled && item.onMouseLeave?.(e, item)}
        >
          {item.component}
        </div>
      );
    }
    const node = (
      <div
        key={item.key}
        className={cn(
          'wg-menu__item',
          {
            'wg-menu__item--disabled': item.disabled,
            'wg-menu__item--danger': item.status === 'danger',
          },
          item.className,
          menuItemClassName,
        )}
        style={item?.style}
        onClick={(e: React.MouseEvent) => !item.disabled && handleClick(e, item)}
        onMouseEnter={(e: React.MouseEvent) => !item.disabled && item.onMouseEnter?.(e, item)}
        onMouseLeave={(e: React.MouseEvent) => !item.disabled && item.onMouseLeave?.(e, item)}
      >
        {item.icon}
        {item.prefix}
        {item.text && <span className="wg-menu__item-text">{item.text}</span>}
        {item.suffix}
      </div>
    );

    if (item.popoverProps) {
      return (
        <Popover {...(item.popoverProps as PopoverProps)} key={item.key}>
          {node}
        </Popover>
      );
    }

    if (item.tooltipProps) {
      return (
        <Tooltip {...(item.tooltipProps as TooltipProps)} key={item.key}>
          {node}
        </Tooltip>
      );
    }

    return <Fragment key={item.key}>{node}</Fragment>;
  };

  const renderSubmenu = (item: MenuSubMenu) => {
    return (
      <div key={item.key}>
        <Trigger
          popup={() => {
            // eslint-disable-next-line ts/no-use-before-define
            return <MenuComponent menuItems={item.children} />;
          }}
          position="right"
          popupAlign={{ right: 8 }}
          childrenPrefix="submenu-selected"
        >
          <div className={cn('wg-menu__item', item.className, menuItemClassName)} style={{ padding: 10 }}>
            {item.icon}
            {item.prefix}
            <span className="wg-menu__item-text">{item.text}</span>
            {item.suffix}
            <span style={{ fontSize: 16 }}>→</span>
          </div>
        </Trigger>
      </div>
    );
  };

  const renderGroup = (item: MenuGroup) => {
    return (
      <div className="wg-menu__group" key={item.key}>
        {item.text && <div className="wg-menu__group-title">{item.text}</div>}
        {
          // eslint-disable-next-line ts/no-use-before-define
          item.render ? item.render({ onAction }) : item.children.map(item => renderItems(item))
        }
      </div>
    );
  };

  const renderItems = (item: MenuItem) => {
    if (item.type === 'item') {
      return renderNormalItem(item);
    }

    if (item.type === 'group') {
      return renderGroup(item);
    }

    if (item.type === 'submenu') {
      return renderSubmenu(item);
    }

    if (item.type === 'divider') {
      return (
        <div className="wg-menu__divider" key={item.key}>
          <div className="wg-menu__divider-line" />
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={ref} className={cn('wg-menu', className)} style={style}>
      {menuItems.map(item => renderItems(item))}
    </div>
  );
}

const MenuComponent = forwardRef<HTMLDivElement, MenuProps>(Menu);

MenuComponent.displayName = 'Menu';

export default MenuComponent;

export type { MenuGroup, MenuItem, MenuNormalItem, MenuProps, MenuSubMenu };
